import {
  trigger,
  state,
  style,
  animate,
  transition,
  query,
  stagger,
  group,
  animateChild,
  keyframes
} from '@angular/animations';

/**
 * Regiment Transitions
 * 
 * Shared element transition animations for the Weekly Regiment → Daily Training flow.
 * Creates a "drill-down" effect where clicking a training item expands it into the daily view.
 */

// Timing constants
const HERO_DURATION = '450ms';
const SECONDARY_DURATION = '300ms';
const STAGGER_DELAY = '50ms';
const EASE_OUT_EXPO = 'cubic-bezier(0.16, 1, 0.3, 1)';
const EASE_ELASTIC = 'cubic-bezier(0.35, 0, 0.25, 1)';

/**
 * Training item list animation - controls the collapse of non-selected items
 */
export const trainingListAnimation = trigger('trainingList', [
  // Initial state
  state('default', style({
    opacity: 1,
    transform: 'translateY(0) scale(1)'
  })),

  // When an item is selected, others collapse
  state('collapsed', style({
    opacity: 0,
    transform: 'translateY(20px) scale(0.95)'
  })),

  // Selected item expands
  state('selected', style({
    opacity: 1,
    transform: 'translateY(0) scale(1)',
    zIndex: 100
  })),

  // Transitions
  transition('default => collapsed', [
    animate(`${SECONDARY_DURATION} ${EASE_OUT_EXPO}`)
  ]),

  transition('default => selected', [
    animate(`${HERO_DURATION} ${EASE_OUT_EXPO}`)
  ]),

  transition('collapsed => default', [
    animate(`${SECONDARY_DURATION} ${EASE_OUT_EXPO}`)
  ])
]);

/**
 * Hero icon animation - the icon that morphs between views
 */
export const heroIconAnimation = trigger('heroIcon', [
  state('origin', style({
    transform: 'translate(0, 0) scale(1)',
    opacity: 1
  })),

  state('flying', style({
    transform: 'translate(var(--hero-translate-x, 0), var(--hero-translate-y, 0)) scale(1.5)',
    opacity: 1,
    position: 'fixed',
    zIndex: 9999
  })),

  state('destination', style({
    transform: 'translate(0, 0) scale(1)',
    opacity: 1
  })),

  transition('origin => flying', [
    animate(`${HERO_DURATION} ${EASE_OUT_EXPO}`)
  ]),

  transition('flying => destination', [
    animate(`${SECONDARY_DURATION} ${EASE_ELASTIC}`)
  ])
]);

/**
 * Page transition animation for router outlet
 */
export const routeAnimations = trigger('routeAnimations', [
  // Weekly Regiment → Daily Training
  transition('regiment => details', [
    // Set up initial positioning
    query(':enter, :leave', [
      style({
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%'
      })
    ], { optional: true }),

    // Animate the leaving view
    query(':leave', [
      style({ opacity: 1 }),
      animate(`${SECONDARY_DURATION} ${EASE_OUT_EXPO}`, style({
        opacity: 0,
        transform: 'scale(0.95)'
      }))
    ], { optional: true }),

    // Animate the entering view
    query(':enter', [
      style({
        opacity: 0,
        transform: 'translateX(30px)'
      }),
      animate(`${HERO_DURATION} ${EASE_ELASTIC}`, style({
        opacity: 1,
        transform: 'translateX(0)'
      }))
    ], { optional: true })
  ]),

  // Daily Training → Weekly Regiment (reverse)
  transition('details => regiment', [
    query(':enter, :leave', [
      style({
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%'
      })
    ], { optional: true }),

    query(':leave', [
      style({ opacity: 1 }),
      animate(`${SECONDARY_DURATION} ${EASE_OUT_EXPO}`, style({
        opacity: 0,
        transform: 'translateX(30px)'
      }))
    ], { optional: true }),

    query(':enter', [
      style({
        opacity: 0,
        transform: 'scale(0.95)'
      }),
      animate(`${HERO_DURATION} ${EASE_ELASTIC}`, style({
        opacity: 1,
        transform: 'scale(1)'
      }))
    ], { optional: true })
  ])
]);

/**
 * Staggered list entrance animation
 */
export const staggeredListAnimation = trigger('staggeredList', [
  transition(':enter', [
    query('.stagger-item', [
      style({ opacity: 0, transform: 'translateY(30px)' }),
      stagger(STAGGER_DELAY, [
        animate(`${SECONDARY_DURATION} ${EASE_OUT_EXPO}`, style({
          opacity: 1,
          transform: 'translateY(0)'
        }))
      ])
    ], { optional: true })
  ])
]);

/**
 * Content slide animation - for content cards
 */
export const contentSlideAnimation = trigger('contentSlide', [
  transition(':enter', [
    style({
      opacity: 0,
      transform: 'translateX(50px)'
    }),
    animate(`${HERO_DURATION} 150ms ${EASE_ELASTIC}`, style({
      opacity: 1,
      transform: 'translateX(0)'
    }))
  ]),

  transition(':leave', [
    animate(`${SECONDARY_DURATION} ${EASE_OUT_EXPO}`, style({
      opacity: 0,
      transform: 'translateX(-30px)'
    }))
  ])
]);

/**
 * Fade transition for header text
 */
export const headerFadeAnimation = trigger('headerFade', [
  transition(':enter', [
    style({ opacity: 0, transform: 'translateY(-10px)' }),
    animate(`${SECONDARY_DURATION} ${EASE_OUT_EXPO}`, style({
      opacity: 1,
      transform: 'translateY(0)'
    }))
  ]),

  transition(':leave', [
    animate(`${SECONDARY_DURATION} ${EASE_OUT_EXPO}`, style({
      opacity: 0,
      transform: 'translateY(-10px)'
    }))
  ])
]);

/**
 * Training item expand animation - used when clicking an item
 */
export const trainingItemExpandAnimation = trigger('trainingItemExpand', [
  state('normal', style({
    transform: 'scale(1)',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.04)'
  })),

  state('expanding', style({
    transform: 'scale(1.02)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
    zIndex: 10
  })),

  transition('normal => expanding', [
    animate('150ms ease-out')
  ]),

  transition('expanding => normal', [
    animate('200ms ease-in')
  ])
]);

/**
 * Overlay fade animation
 */
export const overlayFadeAnimation = trigger('overlayFade', [
  transition(':enter', [
    style({ opacity: 0 }),
    animate('200ms ease-out', style({ opacity: 1 }))
  ]),

  transition(':leave', [
    animate('150ms ease-in', style({ opacity: 0 }))
  ])
]);
